import { CONTRACT_STATUS_ACCEPTED } from "@lhd/components/SideBar/constants";
import { ROUTES } from "@lhd/constants";
import { TargetTabString } from "@lhd/features/Salary/AnnualPermissions/enum";
import { user } from "@lhd/features/users/selector";
import { ContractStatusEnums, Company as TypeCompany } from "@lhd/types";
import { getAccessToken, getCompanyId } from "@lhd/utility";
import { get, toString } from "lodash";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  useFetchAllAppPermissionCurrentUser,
  useFetchAppraisalPermission,
} from "./appPermission";
import { useSalaryPermission } from "./config";
import { useFetchCurrentUserELearningPermissions } from "./eLearningPermission";
import { useCurrentUserPermissions } from "./permissions";
import { useGetAuth } from "./useGetAuth";

export const getIsCompanyPoliciesPurchaseBlocked = (
  contractStatus: string | undefined,
  companyPolicyEnabled: boolean | undefined,
) => {
  const isTrialOrOfficialCompany =
    contractStatus === ContractStatusEnums.TRIAL ||
    contractStatus === ContractStatusEnums.OFFICIAL;
  const isForcedNotPurchasedCompany =
    contractStatus === ContractStatusEnums.NEW_CONTRACT ||
    contractStatus === ContractStatusEnums.CONTRACT_TERMINATED;
  return (
    isForcedNotPurchasedCompany ||
    (isTrialOrOfficialCompany && !companyPolicyEnabled)
  );
};

export const PERMISSION_KEYS = {
  SALARY_MANAGEMENT_ADMIN: "salaryManagementAdmin",
  FULL_SALARY: "fullSalary",
  SALARY_INPUT_ACCESS: "salaryInputAccess",
  SALARY_VIEW_ONLY: "salaryViewOnly",
} as const;

export const useGetPermissions = () => {
  const auth = useSelector(user);
  const { isSuperAdmin, isSpecialSuperAdmin, isUserResigned } = useGetAuth();
  const currentContractStatus = auth?.current_contract_status;

  const canChangeUser = Boolean(auth?.can_change_user || auth?.is_admin);
  const isAdmin = !!auth?.is_admin;
  const isTrial = currentContractStatus === ContractStatusEnums.TRIAL;
  const isOfficial = currentContractStatus === ContractStatusEnums.OFFICIAL;

  const isTrialOrOfficial = isTrial || isOfficial;

  const isContractStatusAccepted = CONTRACT_STATUS_ACCEPTED.includes(
    currentContractStatus as typeof CONTRACT_STATUS_ACCEPTED[number],
  );

  const workerApplicationUsage = get(auth, "worker_application_usage");

  const basicPackageEnabled =
    (get(workerApplicationUsage || {}, "basic_features", false) as boolean) ||
    currentContractStatus === ContractStatusEnums.UN_CONTRACTED;
  const companyPolicyEnabled =
    (get(workerApplicationUsage || {}, "company_policy", false) as boolean) ||
    currentContractStatus === ContractStatusEnums.UN_CONTRACTED;
  const actualKpiUsage =
    (get(workerApplicationUsage || {}, "actual_kpi", false) as boolean) ||
    currentContractStatus === ContractStatusEnums.UN_CONTRACTED;
  const businessControlEnabled =
    (get(workerApplicationUsage || {}, "role_permission", false) as boolean) ||
    currentContractStatus === ContractStatusEnums.UN_CONTRACTED;

  const salaryManagement = get(
    workerApplicationUsage,
    "salary_management",
    false,
  );
  const eLearningUsage = get(workerApplicationUsage, "e_learning", false);
  const appraisalUsage = get(workerApplicationUsage, "appraisal", false);

  const isOnCompanyAdmin = !!auth?.is_on_company_admin;
  const isAdminOfNormalCompany = isAdmin && !isOnCompanyAdmin;
  const currentCompanyLogin = auth?.companies?.find(
    (i: TypeCompany) => Number(i?.id) === Number(getCompanyId()),
  ) as TypeCompany | undefined;
  const isRootCompany = !!currentCompanyLogin?.is_root_company;

  const {
    data: salaryPermissionData,
    isLoading: isSalaryLoading,
    isError: isSalaryError,
  } = useSalaryPermission(!!auth);

  const {
    data: eLearningPermissionData,
    isLoading: isELearningLoading,
    isError: isELearningError,
  } = useFetchCurrentUserELearningPermissions(!!auth);
  const hasAddCoursePermission = eLearningPermissionData?.data
    ?.add_new_course as boolean;
  const hasGeneralEditPermission = eLearningPermissionData?.data
    ?.general_course_edit as boolean;
  const hasGeneralDeletePermission = eLearningPermissionData?.data
    ?.general_course_delete as boolean;
  const hasELearningListPermission =
    isAdmin ||
    !!eLearningPermissionData?.data?.general_course_read ||
    !!eLearningPermissionData?.data?.general_course_edit ||
    !!eLearningPermissionData?.data?.general_course_delete ||
    !!eLearningPermissionData?.data?.add_new_course;

  const {
    data: appPermissions,
    isLoading: isAppLoading,
    isError: isAppError,
  } = useFetchAllAppPermissionCurrentUser(!!auth);
  const {
    data: appraisalPermissionData,
    isLoading: isAppraisalLoading,
    isError: isAppraisalError,
  } = useFetchAppraisalPermission(!!auth);

  const isLoading =
    (!!getAccessToken() && !auth) ||
    (!!auth && !salaryPermissionData && !isSalaryError) ||
    (!!auth && !eLearningPermissionData && !isELearningError) ||
    (!!auth && !appPermissions && !isAppError) ||
    (!!auth && !appraisalPermissionData && !isAppraisalError);

  const hasAppraisalPermission =
    !!appraisalPermissionData?.general_appraisal_delete;
  const isAllowViewApp = !!appPermissions?.add_new_app || isAdmin;

  const salaryEditPermission =
    salaryPermissionData?.salary_edit?.includes("all") ||
    salaryPermissionData?.salary_edit?.length > 0;
  const salaryDeletePermission =
    salaryPermissionData?.salary_delete?.includes("all") ||
    salaryPermissionData?.salary_delete?.length > 0;
  const salaryViewPermission =
    salaryPermissionData?.salary_view?.includes("all") ||
    salaryPermissionData?.salary_view?.length > 0;

  const salaryManagementUser = isContractStatusAccepted && salaryManagement;
  const salaryManagementAdmin =
    isContractStatusAccepted && isAdmin && salaryManagement;
  const fullSalaryPermission =
    Boolean(salaryEditPermission || salaryDeletePermission || isAdmin) &&
    salaryManagementUser;
  const eLearning =
    isContractStatusAccepted && eLearningUsage && hasELearningListPermission;
  const eLearningCourseCreate =
    (isAdmin || hasAddCoursePermission) && eLearningUsage;
  const eLearningCourseEdit =
    (isAdmin ||
      hasAddCoursePermission ||
      hasGeneralEditPermission ||
      hasGeneralDeletePermission) &&
    eLearningUsage;
  const adminConfig = Boolean(
    isAdmin && isRootCompany && isContractStatusAccepted,
  );
  const appraisalAdmin =
    isContractStatusAccepted &&
    (isAdmin || hasAppraisalPermission) &&
    appraisalUsage;
  const appraisal = isContractStatusAccepted && appraisalUsage;

  const salaryInputAccess =
    Boolean(isAdmin || salaryEditPermission || salaryDeletePermission) &&
    (salaryManagementUser || isContractStatusAccepted);

  const salaryViewOnlyAccess =
    isContractStatusAccepted &&
    Boolean(
      isAdmin ||
        salaryViewPermission ||
        salaryEditPermission ||
        salaryDeletePermission,
    );

  return {
    [ROUTES.BRANCH_CHART]: isAdmin && isContractStatusAccepted,
    [ROUTES.KPI]: isAdmin && isContractStatusAccepted && actualKpiUsage,
    defaultBasicPackage: basicPackageEnabled && isContractStatusAccepted,
    [ROUTES.PROJECT_MANAGE]:
      workerApplicationUsage?.project_management && isContractStatusAccepted,
    [ROUTES.ORGS]: basicPackageEnabled && isContractStatusAccepted,
    [ROUTES.BRANCH_CHART]: basicPackageEnabled && isContractStatusAccepted,
    [ROUTES.ORGANIZATION_CHART]:
      basicPackageEnabled && isContractStatusAccepted,
    [ROUTES.CUSTOMER_LIST]: basicPackageEnabled && isContractStatusAccepted,
    [ROUTES.ANNUAL_ORG_SUMMARIES]: isTrialOrOfficial
      ? basicPackageEnabled
      : true,

    [ROUTES.ANNUAL_SALARY_LIST]: salaryManagementUser,
    [ROUTES.ANNUAL_PAYMENT_LIST]: salaryManagementUser,
    [ROUTES.ANNUAL_SALARY_SUMMARIES]: isContractStatusAccepted,
    [PERMISSION_KEYS.SALARY_MANAGEMENT_ADMIN]: salaryManagementAdmin,
    [PERMISSION_KEYS.FULL_SALARY]: fullSalaryPermission,
    [PERMISSION_KEYS.SALARY_INPUT_ACCESS]: salaryInputAccess,
    [PERMISSION_KEYS.SALARY_VIEW_ONLY]: salaryViewOnlyAccess,

    [ROUTES.Electronic_Learning]: eLearning,
    [ROUTES.ELECTRONIC_LEARNING_CHART]: true,
    salarySummariesAccess:
      isContractStatusAccepted &&
      (Boolean(salaryManagement) ||
        currentContractStatus === ContractStatusEnums.UN_CONTRACTED),
    eLearningChartAccess:
      isContractStatusAccepted &&
      (Boolean(eLearningUsage) ||
        currentContractStatus === ContractStatusEnums.UN_CONTRACTED),
    [ROUTES.COMPANY_POLICIES_CHART]: true,
    eLearningStore:
      isContractStatusAccepted && eLearningUsage && hasELearningListPermission,
    eLearningCourseCreate,
    eLearningCourseEdit,
    [ROUTES.COURSE_DETAIL]: eLearningUsage,

    [ROUTES.WORKERS_CREATE]: canChangeUser && !isUserResigned,
    [ROUTES.WORKER_SHEETS]: !isUserResigned,
    canChangeUser,

    [ROUTES.MAINTENANCE_NOTIFICATION]: adminConfig,

    appraisalAdmin,
    appraisal,
    [ROUTES.APPRAISAL_LIST]: appraisal,
    [ROUTES.ROLE_PERMISSIONS]: businessControlEnabled,
    [ROUTES.APPRAISAL_KGI_LIST]: appraisal,
    [ROUTES.APPRAISAL_SHEET_LIST]: appraisal,
    [ROUTES.APPRAISAL_SETTING]: appraisalAdmin,
    [ROUTES.APPS]: isContractStatusAccepted,
    appCreate: isAllowViewApp,

    [ROUTES.PERMISSIONS_MANAGEMENT]: isContractStatusAccepted && isAdmin,
    [ROUTES.PERMISSION_SUMMARIES]: isContractStatusAccepted,
    [ROUTES.AGENT_SUMMARIES]: isContractStatusAccepted,
    [ROUTES.BUSINESS_RPG]: true,
    [ROUTES.BUSINESS_RPG_PLAY]: true,

    [ROUTES.SETTING_CATEGORIES]:
      isAdmin || isSuperAdmin || salaryDeletePermission,
    [ROUTES.PROJECT_MANAGE]:
      isContractStatusAccepted && workerApplicationUsage?.project_management,

    [ROUTES.ORGANIZATION_MANAGEMENT]:
      isAdmin && isContractStatusAccepted && basicPackageEnabled,
    operation: isOnCompanyAdmin || isAdmin, //運営'

    companyCreate: isContractStatusAccepted,

    isContractStatusAccepted,
    isTrialOrOfficial,
    basicPackageEnabled,
    companyPolicyEnabled,
    businessControlEnabled,
    salaryEditPermission,
    salaryDeletePermission,
    salaryViewPermission,
    salaryManagementUser,
    [ROUTES.COMPANIES]: isContractStatusAccepted && isSuperAdmin,
    [ROUTES.ADMIN_CONFIG]: isContractStatusAccepted && isSpecialSuperAdmin,
    [ROUTES.APP_CATEGORIES]: isContractStatusAccepted && isSuperAdmin,
    [ROUTES.APP_SUBMITTED_MANAGEMENT]: isContractStatusAccepted && isSuperAdmin,
    [ROUTES.MAINTENANCE_NOTIFICATION]:
      isContractStatusAccepted && isSpecialSuperAdmin,
    isLoading,
  };
};

export const useGetPermissionsEachPage = () => {
  const { workerId } = useParams();
  const {
    isUserResigned,
    auth,
    isAdmin,
    isSuperAdmin,
    getCurrentWorkerWithIdParams,
  } = useGetAuth();
  const currentContractStatus = auth?.current_contract_status;
  const currentWorker = auth?.workers?.find(
    (i) => Number(i?.company_id) === Number(getCompanyId()),
  );
  const isContractStatusAccepted = CONTRACT_STATUS_ACCEPTED.includes(
    currentContractStatus as typeof CONTRACT_STATUS_ACCEPTED[number],
  );
  const isUnContractedCompany =
    currentContractStatus === ContractStatusEnums.UN_CONTRACTED;
  const canChangeOrgChart = !!auth?.can_change_org_chart;
  const isCurrentWorkerWithId = getCurrentWorkerWithIdParams(
    toString(workerId),
  );
  // const permissionAcceptView = isUserResigned
  //   ? isCurrentWorkerWithId
  //   : isCurrentWorkerWithId || isAdmin || isSuperAdmin;
  const permissionAcceptView = true;

  const {
    data: dataAppPermisionPolicies,
    isFetching,
    isLoading,
  } = useCurrentUserPermissions(
    TargetTabString.COMPANY_POLICIES,
    !!currentWorker?.id &&
      !(isAdmin && isContractStatusAccepted) &&
      !isUserResigned,
  );

  const {
    data: dataAppPermissionRole,
    isFetching: isFetchingPermissionRole,
    isLoading: isLoadingPermissionRole,
  } = useCurrentUserPermissions(TargetTabString.ROLE_PERMISSIONS);

  const {
    data: dataAppPermissionFinancialReports,
    isFetching: isFetchingPermissionFinancialReports,
    isLoading: isLoadingPermissionFinancialReports,
  } = useCurrentUserPermissions(TargetTabString.FINANCIAL_REPORTS);

  const {
    data: dataAppPermissionEmploymentContract,
    isFetching: isFetchingPermissionEmploymentContract,
    isLoading: isLoadingPermissionEmploymentContract,
  } = useCurrentUserPermissions(TargetTabString.EMPLOYEMNT_CONTRACT);
  const {
    data: dataAppPermissionAgreements,
    isFetching: isFetchingPermissionAgreements,
    isLoading: isLoadingPermissionAgreements,
  } = useCurrentUserPermissions(TargetTabString.AGREEMENTS);
  const {
    data: dataAppPermissionLaborDocuments,
    isFetching: isFetchingPermissionLaborDocuments,
    isLoading: isLoadingPermissionLaborDocuments,
  } = useCurrentUserPermissions(TargetTabString.LABOR_DOCUMENTS);
  const {
    data: dataAppBusinessFlowChart,
    isFetching: isFetchingPermissionBusinessFlowChart,
    isLoading: isLoadingPermissionBusinessFlowChart,
  } = useCurrentUserPermissions(TargetTabString.BUSSINESS_FLOWCHARTS);
  const {
    data: dataAppBusinessManuals,
    isFetching: isFetchingPermissionBusinessManuals,
    isLoading: isLoadingPermissionBusinessManuals,
  } = useCurrentUserPermissions(TargetTabString.BUSSINESS_MANUALS);

  const currentCompanyPoliciesPermission =
    dataAppPermisionPolicies?.data?.permissions?.company_policies;

  const currentCompanyRolePermission =
    dataAppPermissionRole?.data?.permissions?.role_permissions;

  const currentFinancialReportsPermission =
    dataAppPermissionFinancialReports?.data?.permissions?.financial_reports;
  const currentEmploymentContractPermission =
    dataAppPermissionEmploymentContract?.data?.permissions
      ?.employment_contracts;
  const currentAgreementsPermission =
    dataAppPermissionAgreements?.data?.permissions?.agreements;
  const currentLaborDocumentsPermission =
    dataAppPermissionLaborDocuments?.data?.permissions?.labor_documents;
  const currentBusinessFlowChartPermission =
    dataAppBusinessFlowChart?.data?.permissions?.business_flowcharts;
  const currentBusinessManualsPermission =
    dataAppBusinessManuals?.data?.permissions?.business_manuals;
  const workerApplicationUsage = get(auth, "worker_application_usage");
  const basicPackageEnabled = get(
    workerApplicationUsage,
    "basic_features",
    false,
  );

  return {
    [ROUTES.COMPANY_POLICIES]: {
      isLoading: isFetching,
      create:
        (isAdmin || currentCompanyPoliciesPermission?.general_delete) &&
        isContractStatusAccepted,
      edit:
        (isAdmin ||
          currentCompanyPoliciesPermission?.general_edit ||
          currentCompanyPoliciesPermission?.general_delete) &&
        isContractStatusAccepted,
      view:
        (isAdmin ||
          currentCompanyPoliciesPermission?.can_edit ||
          currentCompanyPoliciesPermission?.can_delete ||
          currentCompanyPoliciesPermission?.can_view ||
          isFetching ||
          isLoading ||
          !dataAppPermisionPolicies) &&
        isContractStatusAccepted,
      delete:
        (isAdmin || currentCompanyPoliciesPermission?.general_delete) &&
        isContractStatusAccepted,
      all:
        (isAdmin || currentCompanyPoliciesPermission?.general_delete) &&
        isContractStatusAccepted,
    },
    [ROUTES.BUSINESS_TRIP_REPORTS]: {
      create: isContractStatusAccepted,
      edit: isContractStatusAccepted,
      view: isContractStatusAccepted,
      delete: isContractStatusAccepted,
      all: isContractStatusAccepted,
    },
    [ROUTES.BUSINESS_MANUALS]: {
      create:
        (isAdmin ||
          isSuperAdmin ||
          currentBusinessManualsPermission?.general_delete) &&
        isContractStatusAccepted &&
        !isUserResigned,
      edit:
        (isAdmin ||
          isSuperAdmin ||
          currentBusinessManualsPermission?.general_edit) &&
        isContractStatusAccepted &&
        !isUserResigned,
      view:
        (isAdmin ||
          isSuperAdmin ||
          currentBusinessManualsPermission?.can_view ||
          isFetchingPermissionBusinessManuals ||
          isLoadingPermissionBusinessManuals ||
          !dataAppBusinessManuals) &&
        isContractStatusAccepted &&
        !isUserResigned,
      delete:
        (isAdmin ||
          isSuperAdmin ||
          currentBusinessManualsPermission?.general_delete) &&
        isContractStatusAccepted &&
        !isUserResigned,
      all:
        (isAdmin ||
          isSuperAdmin ||
          currentBusinessManualsPermission?.general_delete) &&
        isContractStatusAccepted &&
        !isUserResigned,
      isLoading:
        isFetchingPermissionBusinessManuals ||
        isLoadingPermissionBusinessManuals,
    },
    [ROUTES.ROLE_PERMISSIONS]: {
      create:
        (isAdmin || currentCompanyRolePermission?.general_delete) &&
        isContractStatusAccepted &&
        workerApplicationUsage?.role_permission,
      edit:
        (isAdmin || currentCompanyRolePermission?.general_edit) &&
        isContractStatusAccepted &&
        workerApplicationUsage?.role_permission,
      view:
        (isAdmin ||
          currentCompanyRolePermission?.can_view ||
          isFetchingPermissionRole ||
          isLoadingPermissionRole ||
          !dataAppPermissionRole) &&
        isContractStatusAccepted &&
        workerApplicationUsage?.role_permission,
      delete:
        (isAdmin || currentCompanyRolePermission?.general_delete) &&
        isContractStatusAccepted &&
        workerApplicationUsage?.role_permission,
      all:
        (isAdmin || currentCompanyRolePermission?.general_delete) &&
        isContractStatusAccepted &&
        workerApplicationUsage?.role_permission,
      isLoading: isLoadingPermissionRole,
    },
    [ROUTES.ORGANIZATION_CHART]: {
      create:
        (isAdmin && isContractStatusAccepted && basicPackageEnabled) ||
        (isUnContractedCompany && (isAdmin || canChangeOrgChart)),
      edit:
        (isAdmin && isContractStatusAccepted && basicPackageEnabled) ||
        (isUnContractedCompany && (isAdmin || canChangeOrgChart)),
      view:
        (isContractStatusAccepted && basicPackageEnabled) ||
        (isUnContractedCompany && (isAdmin || canChangeOrgChart)),
      delete:
        (isAdmin && isContractStatusAccepted && basicPackageEnabled) ||
        (isUnContractedCompany && (isAdmin || canChangeOrgChart)),
      all:
        (isContractStatusAccepted && basicPackageEnabled) ||
        (isUnContractedCompany && (isAdmin || canChangeOrgChart)),
    },

    [ROUTES.EMPLOYMENT_CONTRACTS]: {
      create:
        (isAdmin ||
          currentEmploymentContractPermission?.can_delete ||
          currentEmploymentContractPermission?.general_delete) &&
        isContractStatusAccepted,
      edit:
        (isAdmin ||
          currentEmploymentContractPermission?.can_edit ||
          currentEmploymentContractPermission?.can_delete ||
          currentEmploymentContractPermission?.general_edit ||
          currentEmploymentContractPermission?.general_delete) &&
        isContractStatusAccepted,
      view:
        (isAdmin ||
          currentEmploymentContractPermission?.can_edit ||
          currentEmploymentContractPermission?.can_delete ||
          currentEmploymentContractPermission?.can_view ||
          isFetchingPermissionEmploymentContract ||
          isLoadingPermissionEmploymentContract ||
          !dataAppPermissionEmploymentContract) &&
        isContractStatusAccepted,
      delete:
        (isAdmin ||
          currentEmploymentContractPermission?.can_delete ||
          currentEmploymentContractPermission?.general_delete) &&
        isContractStatusAccepted,
      all:
        (isAdmin ||
          currentEmploymentContractPermission?.can_delete ||
          currentEmploymentContractPermission?.general_delete) &&
        isContractStatusAccepted,
    },
    [ROUTES.AGREEMENTS]: {
      create:
        (isAdmin ||
          isSuperAdmin ||
          currentAgreementsPermission?.can_delete ||
          currentAgreementsPermission?.general_delete) &&
        isContractStatusAccepted,
      edit:
        (isAdmin ||
          currentAgreementsPermission?.can_edit ||
          currentAgreementsPermission?.can_delete ||
          currentAgreementsPermission?.general_edit ||
          currentAgreementsPermission?.general_delete) &&
        isContractStatusAccepted,
      view:
        (isAdmin ||
          isSuperAdmin ||
          currentAgreementsPermission?.can_edit ||
          currentAgreementsPermission?.can_delete ||
          currentAgreementsPermission?.can_view ||
          isFetchingPermissionAgreements ||
          isLoadingPermissionAgreements ||
          !dataAppPermissionAgreements) &&
        isContractStatusAccepted,
      delete:
        (isAdmin ||
          isSuperAdmin ||
          currentAgreementsPermission?.can_delete ||
          currentAgreementsPermission?.general_delete) &&
        isContractStatusAccepted,
      all:
        (isAdmin ||
          isSuperAdmin ||
          currentAgreementsPermission?.can_delete ||
          currentAgreementsPermission?.general_delete) &&
        isContractStatusAccepted,
    },
    [ROUTES.LABOR_DOCUMENTS]: {
      create:
        (isAdmin ||
          isSuperAdmin ||
          currentLaborDocumentsPermission?.can_delete ||
          currentLaborDocumentsPermission?.general_delete) &&
        isContractStatusAccepted,
      edit:
        (isAdmin ||
          isSuperAdmin ||
          currentLaborDocumentsPermission?.can_edit ||
          currentLaborDocumentsPermission?.can_delete ||
          currentLaborDocumentsPermission?.general_edit ||
          currentLaborDocumentsPermission?.general_delete) &&
        isContractStatusAccepted,
      view:
        (isAdmin ||
          isSuperAdmin ||
          currentLaborDocumentsPermission?.can_edit ||
          currentLaborDocumentsPermission?.can_delete ||
          currentLaborDocumentsPermission?.can_view ||
          isFetchingPermissionLaborDocuments ||
          isLoadingPermissionLaborDocuments ||
          !dataAppPermissionLaborDocuments) &&
        isContractStatusAccepted,
      delete:
        (isAdmin ||
          isSuperAdmin ||
          currentLaborDocumentsPermission?.can_delete ||
          currentLaborDocumentsPermission?.general_delete) &&
        isContractStatusAccepted,
      all:
        (isAdmin ||
          isSuperAdmin ||
          currentLaborDocumentsPermission?.can_delete ||
          currentLaborDocumentsPermission?.general_delete) &&
        isContractStatusAccepted,
    },

    [ROUTES.FINANCIAL_REPORTS]: {
      create:
        (isAdmin ||
          currentFinancialReportsPermission?.can_delete ||
          currentFinancialReportsPermission?.general_delete) &&
        isContractStatusAccepted,
      edit:
        (isAdmin ||
          currentFinancialReportsPermission?.can_edit ||
          currentFinancialReportsPermission?.can_delete ||
          currentFinancialReportsPermission?.general_edit ||
          currentFinancialReportsPermission?.general_delete) &&
        isContractStatusAccepted,
      view:
        (isAdmin ||
          currentFinancialReportsPermission?.can_edit ||
          currentFinancialReportsPermission?.can_delete ||
          currentFinancialReportsPermission?.can_view ||
          isFetchingPermissionFinancialReports ||
          isLoadingPermissionFinancialReports ||
          !dataAppPermissionFinancialReports) &&
        isContractStatusAccepted,
      delete:
        (isAdmin ||
          currentFinancialReportsPermission?.can_delete ||
          currentFinancialReportsPermission?.general_delete) &&
        isContractStatusAccepted,
      all:
        (isAdmin ||
          currentFinancialReportsPermission?.can_delete ||
          currentFinancialReportsPermission?.general_delete) &&
        isContractStatusAccepted,
    },
    [ROUTES.BUSINESS_FLOW]: {
      create:
        (isAdmin ||
          isSuperAdmin ||
          currentBusinessFlowChartPermission?.general_delete) &&
        isContractStatusAccepted,
      edit:
        (isAdmin ||
          isSuperAdmin ||
          currentBusinessFlowChartPermission?.general_edit) &&
        isContractStatusAccepted,
      view:
        (isAdmin ||
          isSuperAdmin ||
          currentBusinessFlowChartPermission?.can_view ||
          isFetchingPermissionBusinessFlowChart ||
          isLoadingPermissionBusinessFlowChart ||
          !dataAppBusinessFlowChart) &&
        isContractStatusAccepted,
      delete:
        (isAdmin ||
          isSuperAdmin ||
          currentBusinessFlowChartPermission?.general_delete) &&
        isContractStatusAccepted,
      all:
        (isAdmin ||
          isSuperAdmin ||
          currentBusinessFlowChartPermission?.general_delete) &&
        isContractStatusAccepted,
      isLoading:
        isFetchingPermissionBusinessFlowChart ||
        isLoadingPermissionBusinessFlowChart,
    },
  };
};
